/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import AllReportsFilter from "./AllReportsFilter";
import fetchHelper from "../../services/fetchHelper";
import DownloadTable from "./DownloadTable";
import OtherTable from "./OtherTable";
import Pagination from "../../components/Pagination";
import useError from "../../hooks/useError";
import { ExportListContext } from ".";

const PageLimitSize = 30;

export const Loading = ({ length = 15 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={index}>
      <tr>
        <td colSpan="12"></td>
      </tr>
      <tr>
        <td colSpan="12" style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const Table = ({ tab, data, initialLoad }) => {
  switch (tab) {
    case "90-eligible":
    case "90-processed":
    case "30-eligible":
    case "30-processed":
      return <OtherTable data={data} initialLoad={initialLoad} />;
    default:
      return <DownloadTable data={data} initialLoad={initialLoad} tab={tab} />;
  }
};

const AllReports = ({ activeTab, cancelRequest }) => {
  const { setCheckedItems } = useContext(ExportListContext);
  const [filter, setFilter] = useState(null);
  const [defaultFilter, setDefaultFilter] = useState(null);
  const [showAdvance, setShowAdvance] = useState(false);
  const [textSearch, setTextSearch] = useState("");
  const [wildcard, setWildcard] = useState("");
  const [data, setData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [to, setTo] = useState(null);
  const [total, setTotal] = useState(null);
  const [sort, setSort] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [initOffest, setInitOffset] = useState(true);
  const showDefaultApiError = useError();

  useEffect(() => {
    setFilter(null);
    setOffset(0);
    setWildcard("");
    setTextSearch("");
    setInitialLoad(true);
    setIsLoading(true);
    switch (activeTab) {
      case "downloaded":
        setDefaultFilter({
          exported: { key: "exported", com: "$eq", value: true },
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "FINAL" },
        });
        setSort(["interrogationDate"]);
        return;
      case "90-eligible":
        setDefaultFilter({
          interrogationType: { key: "interrogationType", com: "$eq", value: "REMOTE" },
          nintyDayBillStatus: { key: "nintyDayBillStatus", com: "$eq", value: "BILLABLE" },
          nintyDayProcessed: { key: "nintyDayProcessed", com: "$eq", value: false },
        });
        setSort(["nintyCycleEnd"]);
        return;
      case "90-processed":
        setDefaultFilter({
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "FINAL" },
          nintyDayBillStatus: { key: "nintyDayBillStatus", com: "$eq", value: "BILLABLE" },
          nintyDayProcessed: { key: "nintyDayProcessed", com: "$eq", value: true },
        });
        setSort(["-nintyCycleEnd"]);
        return;
      case "30-eligible":
        setDefaultFilter({
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "FINAL" },
          thirtyDayBillStatus: { key: "thirtyDayBillStatus", com: "$eq", value: "BILLABLE" },
          thirtyDayProcessed: { key: "thirtyDayProcessed", com: "$eq", value: false },
        });
        setSort(["thirthyCycleEnd"]);
        return;
      case "30-processed":
        setDefaultFilter({
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "FINAL" },
          thirtyDayBillStatus: { key: "thirtyDayBillStatus", com: "$eq", value: "BILLABLE" },
          thirtyDayProcessed: { key: "thirtyDayProcessed", com: "$eq", value: false },
        });
        setSort(["-thirthyCycleEnd"]);
        return;
      case "weekly":
        setDefaultFilter({ category: { key: "category", com: "$eq", value: "BILLING" } });
        setSort(["-weeklyDate", "-location.id"]);
        return;
      default:
        setDefaultFilter({
          exported: { key: "exported", com: "$eq", value: false },
          interrogationStatus: { key: "interrogationStatus", com: "$eq", value: "FINAL" },
        });
        setSort(["interrogationDate"]);
        return;
    }
  }, [activeTab]);

  const mapFilterKeys = {
    "patient.name": "fname",
    "patient.family": "lname",
    "patient.dob": "birth",
    "location.name": "location",
    mrn: "mrn",
    "device.modelName": "model",
    "device.serialNumber": "serial",
    "device.deviceType": "type",
    "device.manufacturerName": "manufacturer",
    interrogationStatus: "status",
    visitNumber: "visit",
    manufacturerAlert: "manufacturerAlert",
    id: "id",
    "readingPhysician.id": "assignedTo",
    thirtyDayBillStatus: "thirtyStatus",
    nintyDayBillStatus: "nintyStatus",
    interrogationType: "interrogationType",
    nurseFollowUp: "nurseFollowUp",
  };

  const mapKeyFilters = {
    fname: { key: "patient.name", com: "$lk" },
    lname: { key: "patient.family", com: "$lk" },
    birth: { key: "patient.dob", com: "$eq" },
    location: { key: "location.name", com: "$eq" },
    mrn: { key: "mrn", com: "$lk" },
    model: { key: "device.modelName", com: "$lk" },
    serial: { key: "device.serialNumber", com: "$lk" },
    type: { key: "device.deviceType", com: "$lk" },
    manufacturer: { key: "device.manufacturerName", com: "$eq" },
    status: { key: "interrogationStatus", com: "$lq" },
    visit: { key: "visitNumber", com: "$lk" },
    manufacturerAlert: { key: "manufacturerAlert", com: "$eq" },
    id: { key: "id", com: "$eq" },
    assignedTo: { key: "readingPhysician.id", com: "$eq" },
    thirtyStatus: { key: "thirtyDayBillStatus", com: "$eq" },
    nintyStatus: { key: "nintyDayBillStatus", com: "$eq" },
    interrogationType: { key: "interrogationType", com: "$lk" },
    nurseFollowUp: { key: "nurseFollowUp", com: "$eq" },
  };

  const onSearchSubmittion = () => {
    let filters = {};
    if (textSearch) {
      const item = (i) => mapKeyFilters[i.split(":")[0]];
      textSearch.split(" ").forEach((i) => {
        if (i.split(":")[0] && i.split(":")[1] && item(i)?.key)
          filters[item(i).key] = { key: item(i).key, com: item(i).com, value: i.split(":")[1] };
      });
      if (Object.keys(filters).length === 0) {
        const wildCard = textSearch
          .replace(/\s\s+/g, " ")
          .split(" ")
          .map((ts) => ({ "patient.name": { $lk: ts }, "patient.family": { $lk: ts } }));
        setTextSearch(textSearch.replace(/\s\s+/g, " "));
        setWildcard(wildCard);
      }
      setIsLoading(true);
    } else {
      setOffset(0);
      setWildcard("");
    }
    setFilter(filters);
    // fetchData();
  };

  useEffect(() => {
    if (defaultFilter) fetchData();
  }, [defaultFilter]);

  useEffect(() => {
    if (!initOffest) {
      setInitialLoad(true);
      fetchData();
    }
  }, [offset]);

  useEffect(() => {
    if (filter) {
      setIsLoading(true);
      setInitialLoad(true);
      const result = Object.keys(filter)
        .map((key) => `${mapFilterKeys[key]}:${filter[key].value}`)
        .join(" ");
      if (result) setTextSearch(result);
      fetchData();
      if (Object.keys(filter).length === 0) {
        if (textSearch.search(":") !== -1) setTextSearch("");
        else setTextSearch(textSearch);
      }
    } else setTextSearch(textSearch);
  }, [filter]);

  const fetchData = async () => {
    try {
      const where = [];
      setCheckedItems([]); // Reset items selection
      Object.keys(defaultFilter).map((f) => {
        const data = defaultFilter[f];
        where.push({ [data.key]: { [data.com]: data.value } });
      });
      if (wildcard) where.push(...wildcard);
      if (filter)
        Object.keys(filter).map((f) => {
          const data = filter[f];
          where.push({ [data.key]: { [data.com]: data.value } });
        });

      const urlParams = {
        where: JSON.stringify(where),
        match: "AND",
        matchInner: "OR",
        sort: sort.join(","),
      };

      const urlSearch = new URLSearchParams(urlParams);

      let url = "/patientmanagement/rest/interrogations/getNewInbox?";
      if (activeTab === "weekly") {
        url = "/patientmanagement/rest/billingweekly?";
      }

      const { data, responseHeaders } = await fetchHelper.get({
        url: url + urlSearch,
        headers: {
          "X-Range": `items=${offset}-${offset + PageLimitSize - 1}`,
          Range: `items=${offset}-${offset + PageLimitSize - 1}`,
        },
        includeResponseHeaders: ["Content-Range"],
        options: {
          signal: cancelRequest.signal,
        },
      });
      setTo(offset + data.length - 1);
      setData(data);
      setTotal(+responseHeaders["Content-Range"].split("/")[1]);
      setInitOffset(false);
    } catch (err) {
      showDefaultApiError(err);
      setData([]);
      console.log(err);
    } finally {
      setInitialLoad(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="content">
        <div className="searchAdv" id="showSearch">
          <div className="search">
            <div className="row rowStretch searchIn framing">
              <div className="col col-s-100 col-m-80 col80 searchInCol">
                <label>
                  <input
                    type="text"
                    placeholder="Type any term(s) to begin search..."
                    value={textSearch}
                    onChange={(e) => setTextSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        onSearchSubmittion();
                      }
                    }}
                  />
                  <button
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      padding: 15,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      onSearchSubmittion();
                    }}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ height: 18, width: 18, filter: "invert()" }}
                      />
                    ) : (
                      <div
                        className="search-icon icon-64"
                        style={{ filter: "invert()", width: "15px", height: "15px" }}
                      ></div>
                    )}
                  </button>
                </label>
              </div>
              {activeTab === "downloaded" ? null : (
                <div className="col col-s-100 col-m-30 col-l-10 col10 advSearchCol">
                  <div className="colBtnAdvSearch" style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      className="btn btnTeal hideAdv"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-around",
                        maxWidth: "200px",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAdvance(!showAdvance);
                      }}
                    >
                      <div id="advanced-search-btn" className="search-icon icon-64"></div>{" "}
                      {showAdvance ? "Hide" : "Show"} Advanced
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {showAdvance ? (
            <AllReportsFilter onFilterChange={(filter) => setFilter(filter)} filter={filter} />
          ) : null}
        </div>
      </div>

      <Table tab={activeTab} data={data} initialLoad={initialLoad} />

      <Pagination
        offset={offset}
        to={to}
        limit={PageLimitSize}
        onSetOffset={setOffset}
        total={total}
        loading={initialLoad}
      />
    </>
  );
};

export default AllReports;
